import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

export const initializeThreeScene = (containerRef, sceneRef, rendererRef, cameraRef, controlsRef) => {
  if (!containerRef.current) return;

  // Create scene
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0xffffff);
  sceneRef.current = scene;

  // Create renderer
  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    powerPreference: "high-performance"
  });
  renderer.setPixelRatio(window.devicePixelRatio);
  renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;

  // Set renderer to not block UI elements
  renderer.domElement.style.position = 'absolute';
  renderer.domElement.style.top = '0';
  renderer.domElement.style.left = '0';
  renderer.domElement.style.zIndex = '1';
  renderer.domElement.style.overflow = 'hidden';

  rendererRef.current = renderer;
  containerRef.current.appendChild(renderer.domElement);

  // Create camera
  const camera = new THREE.PerspectiveCamera(
    32,
    containerRef.current.clientWidth / containerRef.current.clientHeight, 
    0.01,
    10000
  );
  // Use setScalar to match original HTML camera positioning
  camera.position.setScalar(300);
  camera.updateProjectionMatrix();
  cameraRef.current = camera;

  // Create controls
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.1;
  controls.autoRotate = false;
  controlsRef.current = controls;

  // Add lights to match original HTML lighting setup
  const ambient = new THREE.HemisphereLight(0xffffff, 0xbfd4d2, 0.9);
  scene.add(ambient);

  const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
  directionalLight.position.set(5, 10, 7.5).multiplyScalar(3); // Match original HTML
  directionalLight.castShadow = true;
  directionalLight.shadow.mapSize.setScalar(2048); // Match original HTML
  directionalLight.shadow.bias = -1e-4;
  directionalLight.shadow.normalBias = 1e-4;
  scene.add(directionalLight);

  const hemiLight = new THREE.HemisphereLight(0xffffff, 0x8d6e63, 1);
  hemiLight.position.set(0, 100, 0);
  scene.add(hemiLight);

  // Set up shadow camera
  const shadowCam = directionalLight.shadow.camera;
  shadowCam.left = shadowCam.bottom = -500;
  shadowCam.right = shadowCam.top = 500;
  shadowCam.updateProjectionMatrix();

  // Add grid helper
  const gridHelper = new THREE.GridHelper(200, 20, 0x444444, 0x888888); // Changed colors to match original
  gridHelper.material.opacity = 0.4; // Changed to 0.4 to match original HTML
  gridHelper.material.transparent = true;
  gridHelper.position.y = -0.01;
  scene.add(gridHelper);

  // Add axes helper
  const axesHelper = new THREE.AxesHelper(50);
  scene.add(axesHelper);

  // Add shadow plane
  const shadowPlane = new THREE.Mesh(
    new THREE.PlaneGeometry(1000, 1000),
    new THREE.ShadowMaterial({
      transparent: true,
      opacity: 0.075,
      side: THREE.DoubleSide,
    })
  );
  shadowPlane.rotation.x = -Math.PI / 2;
  shadowPlane.receiveShadow = true;
  scene.add(shadowPlane);

  // Start animation loop
  const animate = () => {
    requestAnimationFrame(animate);
    controls.update();
    renderer.render(scene, camera);
  };
  animate();

  // Handle window resize
  const handleResize = () => {
    if (!containerRef.current) return;
    camera.aspect = containerRef.current.clientWidth / containerRef.current.clientHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
  };
  window.addEventListener('resize', handleResize);

  return () => {
    window.removeEventListener('resize', handleResize);
    if (containerRef.current && renderer.domElement.parentNode === containerRef.current) {
      containerRef.current.removeChild(renderer.domElement);
    }
    renderer.dispose();
  };
};